import {
	FinancialPeriodId,
	FinancialPeriodPreparationMethod,
	type ISODate,
	ISODateTime,
	PeriodType,
} from "@inrev/common";
import { FinancialPeriodDto } from "../utils";

type PeriodSortable = {
	periodType: PeriodType;
	preparationMethod: FinancialPeriodPreparationMethod;
	periodEndDate: ISODate;
	createdAt: ISODateTime;
	id: FinancialPeriodId;
};

const compareFinancialPeriodsForColumns = (
	a: PeriodSortable,
	b: PeriodSortable,
	options: { rankPeriodType: boolean },
): number => {
	const dateDiff = b.periodEndDate.localeCompare(a.periodEndDate);
	if (dateDiff !== 0) return dateDiff;

	if (options.rankPeriodType) {
		const typeDiff = PeriodType.rank(a.periodType) - PeriodType.rank(b.periodType);
		if (typeDiff !== 0) return typeDiff;
	}

	const qualityDiff =
		FinancialPeriodPreparationMethod.qualityRank(a.preparationMethod) -
		FinancialPeriodPreparationMethod.qualityRank(b.preparationMethod);
	if (qualityDiff !== 0) return qualityDiff;

	const revisionDiff = b.createdAt.localeCompare(a.createdAt);
	if (revisionDiff !== 0) return revisionDiff;

	return a.id.localeCompare(b.id);
};

const toFinancialPeriodSortable = (period: FinancialPeriodDto): PeriodSortable => ({
	preparationMethod: period.preparationMethod,
	periodType: period.periodType,
	periodEndDate: period.periodEndDate,
	createdAt: period.createdAt,
	id: period.id,
});

/** Column order for balance sheet matrix and period selector (newest / best first). */
export const sortFinancialPeriods = (
	financialPeriods: FinancialPeriodDto[],
): FinancialPeriodDto[] =>
	[...financialPeriods].sort((a, b) =>
		compareFinancialPeriodsForColumns(toFinancialPeriodSortable(a), toFinancialPeriodSortable(b), {
			rankPeriodType: false,
		}),
	);
